<template>
  <div class="space-y-6">
    <form v-if="!mfaChallenge" class="space-y-6" @submit.prevent="onSubmitCredentials">
      <div class="space-y-4">
        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormLabel class="font-medium text-sm">{{ t('auth.login.email-label') }}</FormLabel>
            <FormControl>
              <Input type="email" :placeholder="t('auth.login.email-placeholder')" v-bind="componentField" class="border" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="password">
          <FormItem>
            <FormLabel class="font-medium text-sm">{{ t('auth.login.password-label') }}</FormLabel>
            <FormControl>
              <div class="relative">
                <Input
                  v-bind="componentField"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="t('auth.login.password-placeholder')"
                  maxlength="255"
                  class="border pr-10"
                />
                <FormPasswordEye :password-shown="showPassword" @click="showPassword = !showPassword" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <p class="text-xs text-muted-foreground">
        <NuxtLink to="/forgot-password" class="underline">{{ t('auth.login.forgot-password') }}</NuxtLink>
      </p>

      <p v-if="credentialsError" class="text-sm text-destructive" role="alert">
        {{ credentialsError }}
      </p>

      <Button type="submit" class="w-full" :disabled="isSubmitting">{{ t('auth.login.submit-button') }}</Button>
    </form>

    <form v-else class="space-y-4" @submit.prevent="onSubmitMfa">
      <div class="space-y-2">
        <h2 class="text-center uppercase">{{ t('auth.login.mfa-title') }}</h2>
        <p class="text-sm text-center text-muted-foreground">
          {{ t('auth.login.mfa-description', { destination: mfaChallenge.destination }) }}
        </p>
        <p class="text-xs text-center text-muted-foreground">
          {{ t('auth.login.mfa-expires', { minutes: Math.max(1, Math.ceil(mfaChallenge.expires_in / 60)) }) }}
        </p>
      </div>

      <div class="space-y-2">
        <label class="font-medium text-sm">{{ t('auth.login.mfa-code-label') }}</label>
        <Input
          v-model="verificationCode"
          autocomplete="one-time-code"
          inputmode="numeric"
          :maxlength="10"
          :placeholder="t('auth.login.mfa-code-placeholder')"
          class="border"
        />
      </div>

      <p v-if="mfaError" class="text-sm text-destructive" role="alert">
        {{ mfaError }}
      </p>

      <div class="flex gap-3">
        <Button type="button" variant="outline" class="flex-1" :disabled="isVerifying" @click="resetToCredentials">
          {{ t('auth.login.back-button') }}
        </Button>
        <Button type="submit" class="flex-1" :disabled="isVerifying || !verificationCode.trim()">
          {{ t('auth.login.verify-button') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { FetchError } from 'ofetch'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import ZodMessages from '@@/zod_presets/messages'
import FormPasswordEye from '@/components/ui/form/FormPasswordEye.vue'

type LoginMfaChallenge = {
  challenge_id: string
  channel: string
  destination: string
  expires_in: number
  mfa_required: true
}

const emit = defineEmits<{
  (e: 'login-success'): void
}>()

const { t } = useI18n()
const { $analytics } = useNuxtApp()
const { create, verifyMfaChallenge } = useUser()

const showPassword = ref(false)
const verificationCode = ref('')
const credentialsError = ref('')
const mfaError = ref('')
const mfaChallenge = ref<LoginMfaChallenge | null>(null)
const isVerifying = ref(false)

const formSchema = toTypedSchema(
  z.object({
    email: z.string().email(ZodMessages.email),
    password: z.string().min(1, t('auth.login.password-required')),
  }),
)

const { handleSubmit, isSubmitting } = useForm({
  validationSchema: formSchema,
})

const onSubmitCredentials = handleSubmit(async values => {
  credentialsError.value = ''

  try {
    const response = await create({ email_address: values.email ?? '', password: values.password ?? '' })

    if ('mfa_required' in response) {
      mfaChallenge.value = response
      verificationCode.value = ''
      mfaError.value = ''
      return
    }

    if ('mfa_throttled' in response) {
      credentialsError.value = response.detail
      $analytics?.track('loginError', {
        errorMessage: credentialsError.value,
      })
      return
    }

    emit('login-success')
  } catch (error) {
    credentialsError.value = t('auth.login.invalid-credentials')
    $analytics?.track('loginError', {
      errorMessage: credentialsError.value,
    })
  }
})

const onSubmitMfa = async () => {
  if (!mfaChallenge.value) return

  mfaError.value = ''
  isVerifying.value = true

  try {
    await verifyMfaChallenge({
      challenge_id: mfaChallenge.value.challenge_id,
      code: verificationCode.value.trim(),
    })
    emit('login-success')
  } catch (error) {
    mfaError.value = t('auth.login.mfa-invalid-code')
    $analytics?.track('loginError', {
      errorMessage: mfaError.value,
    })
  } finally {
    isVerifying.value = false
  }
}

function resetToCredentials() {
  mfaChallenge.value = null
  verificationCode.value = ''
  mfaError.value = ''
  credentialsError.value = ''
  showPassword.value = false
}

</script>
